// Ekran panelu rodzica: PIN → profile dzieci i urządzenia (wyłącz / przywróć). Od patcha 49 nie ma tu „Dni lekcji” (edytora, szkiców, nagrań z R2): kurs jest wbudowany w aplikację.
// PIN żyje tylko w pamięci tego ekranu (po wyjściu znika). Dziecko nie ma tu wejścia: panel otwiera się z „Dla rodzica” i wymaga PIN-u.
import { createAdminClient } from '../lib/adminApi.ts';
import type { AdminClient, AdminResult, Overview } from '../lib/adminApi.ts';
import { formatSeen } from '../lib/adminView.ts';
import { renderProfilesSection } from './adminProfiles.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import type { Child } from './dom.ts';

export interface AdminDeps {
  createClient?: (pin: string) => AdminClient;
  now?: () => Date;
}

export function renderAdmin(ctx: AppCtx, deps: AdminDeps = {}): void {
  const make = deps.createClient ?? ((pin: string) => createAdminClient(pin));
  const now = deps.now ?? (() => new Date());
  let client: AdminClient | null = null;
  ctx.at?.({ screen: 'admin' }); // po odświeżeniu panel pyta o PIN od nowa
  ctx.leaveHome(); // odpowiedź serwera z treścią (start aplikacji) nie może wyrzucić rodzica z panelu
  const screen = h('main', { class: 'stage admin', 'data-testid': 'admin' });
  const leave = () => {
    client = null; // PIN znika razem z klientem
    // Rodzic mógł zmienić imię, wygląd albo zarchiwizować profil: odświeżamy listę, zanim dziecko zobaczy ekran główny.
    if (ctx.account) void ctx.account.refresh().then(() => ctx.goHome());
    else ctx.goHome();
  };
  ctx.root.replaceChildren(h('header', { class: 'topbar' }, button('✕', leave, { class: 'icon-btn', title: 'Zamknij panel', testid: 'admin-exit' }), h('div', { class: 'title' }, 'Panel rodzica')), screen);
  const show = (...kids: Child[]) => screen.replaceChildren(...(kids.filter((k): k is Node | string => k !== null && k !== undefined && k !== false && k !== 0)));

  // --- logowanie ---------------------------------------------------------------------------------
  function showLogin(message = ''): void {
    const pin = h('input', { type: 'password', autocomplete: 'off', placeholder: 'PIN rodzica', 'aria-label': 'PIN rodzica', 'data-testid': 'admin-pin' });
    const msg = h('p', { class: 'muted', role: 'status', 'data-testid': 'admin-message' }, message);
    let busy = false;
    const submit = async () => {
      if (busy) return;
      if (!pin.value.trim()) {
        msg.textContent = 'Wpisz PIN rodzica.';
        return;
      }
      busy = true;
      login.disabled = true;
      msg.textContent = 'Sprawdzam…';
      const c = make(pin.value.trim());
      const r = await c.overview();
      busy = false;
      login.disabled = false;
      if (r.ok) {
        client = c;
        pin.value = '';
        showMain(r.data);
      } else msg.textContent = `❌ ${r.message}`;
    };
    const login = button('Wejdź', () => void submit(), { class: 'btn primary', testid: 'admin-login' });
    pin.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') void submit();
    });
    show(h('p', null, 'Wpisz PIN rodzica, żeby zarządzać profilami dzieci i urządzeniami.'), pin, login, msg);
  }

  // --- widok główny ------------------------------------------------------------------------------
  async function reload(flash = ''): Promise<void> {
    if (!client) return showLogin();
    const r = await client.overview();
    if (r.ok) showMain(r.data, flash);
    else showLogin(`❌ ${r.message}`);
  }

  function showMain(ov: Overview, flash = ''): void {
    const c = client as AdminClient;
    const note = h('p', { class: 'muted', role: 'status', 'data-testid': 'admin-message' }, flash);
    const guarded = async (run: () => Promise<AdminResult<unknown>>, ok: string) => {
      const r = await run();
      if (!r.ok) {
        note.textContent = `❌ ${r.message}`;
        return;
      }
      await reload(ok);
    };
    const deviceRows = ov.devices.map((d) =>
      h('li', { class: `adm-row${d.revoked ? ' off' : ''}`, 'data-testid': 'device-row', 'data-device': d.id },
        h('div', { class: 'adm-main' },
          h('strong', null, d.name || '(bez nazwy)'),
          h('span', { class: 'muted' }, ` · ${formatSeen(d.lastSeen, now())} · dziś: ${d.tokensToday}/${ov.limits.dailyTokens}`),
          d.revoked && h('span', { class: 'badge bad' }, 'wyłączone')),
        button(d.revoked ? 'Przywróć' : 'Wyłącz', () => {
          if (!d.revoked && !confirm(`Wyłączyć urządzenie „${d.name || d.id}”? Straci dostęp do dokładnej oceny wymowy, aż je przywrócisz lub sparujesz od nowa.`)) return;
          void guarded(() => c.setDeviceRevoked(d.id, !d.revoked), d.revoked ? 'Urządzenie przywrócone.' : 'Urządzenie wyłączone.');
        }, { class: 'btn ghost', testid: 'device-toggle' })),
    );
    show(
      note,
      renderProfilesSection(c, now),
      h('h2', null, 'Urządzenia'),
      deviceRows.length ? h('ul', { class: 'adm-list', 'data-testid': 'device-list' }, ...deviceRows) : h('p', { class: 'muted' }, 'Żadne urządzenie nie jest jeszcze sparowane.'),
      h('div', { class: 'row' },
        button('Odśwież', () => void reload(), { class: 'btn ghost', testid: 'admin-refresh' }),
        button('Zamknij panel', leave, { class: 'btn link', testid: 'admin-close' })),
      h('p', { class: 'muted' }, `Wersja linijek bota: ${ov.version || '(pusta)'}. Tablety pobierają je przy starcie aplikacji.`),
    );
  }

  showLogin();
}
